import { Skeleton } from '@folio/components';
import { cn } from '@folio/lib';
import type { EmailThreadEmailDto } from 'korint-sdk';
import { Link2, Paperclip } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { InboxDate } from './InboxDate';
import { counterpartOf, STRETCHED_BUTTON } from './threadHelpers';
import { ToAttachBadge } from './ThreadIndicators';
import { Text } from '@folio/components/ui/text';
import { Stack } from '@folio/components/ui/flex';

function EmailRow({
  email,
  isSelected,
  showToAttach,
  onSelect,
}: {
  email: EmailThreadEmailDto;
  isSelected: boolean;
  showToAttach: boolean;
  onSelect: (emailId: string) => void;
}) {
  const { t } = useTranslation();
  const counterpart = counterpartOf(email);
  const isLinked = email.linkedPolicies.length > 0;

  return (
    <li
      className={cn(
        'relative rounded-md px-2.5 py-2 transition-colors hover:bg-muted/70',
        isSelected && 'bg-muted',
        email.isArchived && 'text-muted-foreground',
      )}
      data-testid="general-inbox-thread-email"
      aria-current={isSelected || undefined}
    >
      <button
        type="button"
        className={cn('block w-full', STRETCHED_BUTTON)}
        onClick={() => onSelect(email.id)}
      >
        <span className="flex items-center gap-2">
          <Text
            inline
            truncate
            weight={isSelected ? 'semibold' : 'medium'}
            className="min-w-0 flex-1 text-[13px]"
            title={counterpart.address}
          >
            {counterpart.label}
          </Text>
          {email.isArchived && <span className="sr-only">{t('generalInbox.archived')}</span>}
          {email.hasAttachments && (
            <Paperclip
              className="size-3.5 shrink-0 text-muted-foreground"
              aria-label={t('generalInbox.hasAttachments')}
            />
          )}
          {isLinked && (
            <Link2
              className="size-3.5 shrink-0 text-muted-foreground"
              aria-label={t('generalInbox.linked')}
            />
          )}
          <InboxDate
            className="shrink-0 text-xs tabular-nums text-muted-foreground"
            date={email.date}
          />
        </span>
        {(email.preview || (showToAttach && email.isToProcess)) && (
          <span className="mt-0.5 flex items-center gap-2">
            <Text inline truncate size="xs" tone="muted" className="min-w-0 flex-1">
              {email.preview}
            </Text>
            {showToAttach && email.isToProcess && <ToAttachBadge />}
          </span>
        )}
      </button>
    </li>
  );
}

export function ThreadEmails({
  emails,
  isError,
  isAnalysisPending,
  selectedEmailId,
  onSelectEmail,
}: {
  emails: EmailThreadEmailDto[] | undefined;
  isError: boolean;
  isAnalysisPending: boolean;
  selectedEmailId: string | null;
  onSelectEmail: (emailId: string) => void;
}) {
  const { t } = useTranslation();

  if (isError)
    return (
      <Text size="xs" tone="destructive" role="alert" className="px-2.5">
        {t('generalInbox.threadError')}
      </Text>
    );
  if (!emails) return <Skeleton className="h-12 w-full" />;
  return (
    <Stack asChild>
      <ul className="border-l pl-2 gap-0.5" aria-label={t('generalInbox.threadEmails')}>
        {emails.map(email => (
          <EmailRow
            key={email.id}
            email={email}
            isSelected={email.id === selectedEmailId}
            showToAttach={!isAnalysisPending}
            onSelect={onSelectEmail}
          />
        ))}
      </ul>
    </Stack>
  );
}
